"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { useMemo } from "react";
import { HiOutlineChevronRight } from "react-icons/hi2";
import type { GetTeacherResponse } from "@/openapi/generated";
import { usePublicPageReturnContext } from "@/hooks";
import {
  usePresignedImageErrorRecovery,
  useTeacherPublicProfileImage,
} from "@/features/media/hooks";
import {
  appendPublicReturnQuery,
  buildLocationPublicPath,
} from "@/lib/utils/publicPaths";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Link, NavLink, Text } from "@/components/ui";
import { ImageWithFallback } from "@/components/shared";
import { ClassShareButton } from "@/views/classes/components";
import { PublicDetailBreadcrumbs } from "@/views/shared/public-listing";
import { TeacherClassesSection } from "./TeacherClassesSection";
import { PageTypeGlobal } from "@/lib/types";

interface RelatedLinkProps {
  label: string;
  value: string;
  href?: string;
}

const RelatedLink = ({ label, value, href }: RelatedLinkProps) => {
  const theme = useThemeTokens();

  const inner = (
    <Flex
      align="center"
      justify="space-between"
      gap={4}
      py={4}
      borderBottomWidth="1px"
      borderColor={theme.borderGlass.subtle}
      sx={{
        "@media (hover: hover)": {
          "&:hover [data-related-value]": { color: theme.accent.primary },
          "&:hover [data-related-chevron]": {
            color: theme.accent.primary,
            transform: "translateX(2px)",
          },
        },
      }}
    >
      <Box minW={0}>
        <Text fontSize="xs" color={theme.text.tertiary} mb={1}>
          {label}
        </Text>
        <Text
          data-related-value
          fontSize="sm"
          fontWeight="600"
          color={theme.text.primary}
          transition="color 0.2s ease"
          noOfLines={2}
        >
          {value}
        </Text>
      </Box>
      <Icon
        as={HiOutlineChevronRight}
        data-related-chevron
        boxSize="16px"
        color={theme.text.tertiary}
        flexShrink={0}
        transition="transform 0.2s ease, color 0.2s ease"
        aria-hidden
      />
    </Flex>
  );

  if (!href) return inner;

  return (
    <Link as={NavLink} href={href} display="block" _hover={{ textDecoration: "none" }}>
      {inner}
    </Link>
  );
};

export interface TeacherPublicPageViewProps {
  teacher: GetTeacherResponse;
  teacherId: number;
}

export const TeacherPublicPageView = ({
  teacher,
  teacherId,
}: TeacherPublicPageViewProps) => {
  const theme = useThemeTokens();
  const { returnClassId, fromListing, backLink } = usePublicPageReturnContext({
    page: "teacher",
  });

  const returnQueryContext = useMemo(
    () => ({
      classId: returnClassId,
      teacherId,
      fromListing,
    }),
    [returnClassId, teacherId, fromListing],
  );

  const { url: profileImageUrl, refreshImage } = useTeacherPublicProfileImage(
    teacherId,
    { enabled: teacherId > 0 },
  );
  const handleProfileImageError = usePresignedImageErrorRecovery(
    profileImageUrl,
    refreshImage,
  );

  const biographyText = teacher.biography?.trim() ?? "";

  const { displayHeading, displaySubline } = useMemo(() => {
    const first = (teacher.firstName ?? "").trim();
    const last = (teacher.lastName ?? "").trim();
    const full = `${first} ${last}`.trim();
    const pref = (teacher.preferredName ?? "").trim();
    if (!pref) {
      return { displayHeading: full, displaySubline: null as string | null };
    }
    const prefL = pref.toLowerCase();
    const fullL = full.toLowerCase();
    const firstL = first.toLowerCase();
    if (prefL === fullL || prefL === firstL) {
      return { displayHeading: full, displaySubline: null };
    }
    return { displayHeading: pref, displaySubline: full };
  }, [teacher]);

  const shortName = useMemo(() => {
    return teacher.preferredName?.trim()
      ? teacher.preferredName.trim()
      : `${teacher.firstName} ${teacher.lastName}`;
  }, [teacher]);

  const workLocations = useMemo(
    () =>
      (teacher.workLocations ?? []).filter((loc) => Boolean(loc.name?.trim())),
    [teacher.workLocations],
  );

  const locationMeta = workLocations
    .map((loc) => loc.name?.trim())
    .filter(Boolean)
    .join(" · ");

  return (
    <Flex direction="column" maxW="720px" mx="auto" w="100%">
      <Flex justify="space-between" align="center" gap={3} mb={{ base: 5, md: 6 }}>
        <PublicDetailBreadcrumbs
          parentHref={
            fromListing === "teachers"
              ? backLink.href
              : PageTypeGlobal.Teachers
          }
          parentLabel="Instructors"
          currentLabel={displayHeading}
        />
        <ClassShareButton title={displayHeading} subject="instructor" iconOnlyOnMobile={false} />
      </Flex>

      <Flex justify="center" mb={{ base: 5, md: 6 }}>
        <Box
          w={{ base: "160px", md: "200px" }}
          aspectRatio="3 / 4"
          borderRadius="12px"
          overflow="hidden"
          borderWidth="1px"
          borderColor={theme.borderGlass.default}
          flexShrink={0}
        >
          <ImageWithFallback
            src={profileImageUrl}
            alt={shortName}
            fallbackType="avatar"
            width="100%"
            height="100%"
            borderRadius="0"
            objectFit="cover"
            priority
            onImageError={handleProfileImageError}
          />
        </Box>
      </Flex>

      <Box borderBottomWidth="1px" borderColor={theme.borderGlass.subtle} pb={{ base: 5, md: 6 }}>
        <Text
          fontSize="xs"
          fontWeight="600"
          letterSpacing="0.14em"
          textTransform="uppercase"
          color={theme.text.tertiary}
          mb={2}
        >
          Instructor
        </Text>
        <Text
          as="h1"
          fontSize={{ base: "2xl", md: "3xl" }}
          fontWeight="600"
          letterSpacing="-0.03em"
          color={theme.text.primary}
          lineHeight="1.15"
        >
          {displayHeading}
        </Text>
        {displaySubline ? (
          <Text mt={2} fontSize="md" fontWeight="500" color={theme.text.secondary} lineHeight="1.35">
            {displaySubline}
          </Text>
        ) : null}
        {locationMeta ? (
          <Text mt={4} fontSize="sm" color={theme.text.secondary} lineHeight="1.6">
            {locationMeta}
          </Text>
        ) : null}
      </Box>

      <Box py={{ base: 5, md: 6 }} borderBottomWidth="1px" borderColor={theme.borderGlass.subtle}>
        {biographyText ? (
          <Text fontSize="md" color={theme.text.secondary} lineHeight="1.75" whiteSpace="pre-line">
            {biographyText}
          </Text>
        ) : (
          <Text fontSize="md" color={theme.text.tertiary} lineHeight="1.75" fontStyle="italic">
            Bio coming soon.
          </Text>
        )}
      </Box>

      <TeacherClassesSection teacherId={teacherId} teacherName={displayHeading} />

      {workLocations.length > 0 ? (
        <Box pt={{ base: 2, md: 3 }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color={theme.text.tertiary}
            mb={2}
          >
            Locations
          </Text>
          <Box borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
            {workLocations.map((loc, index) => {
              const name = loc.name?.trim() ?? "";
              const locationId = Number(loc.locationId);
              const isLink = loc.isActive !== false;
              const href = isLink
                ? appendPublicReturnQuery(
                    buildLocationPublicPath({ name, locationId }),
                    returnQueryContext,
                  )
                : undefined;

              return (
                <Box key={String(loc.locationId)}>
                  {isLink ? (
                    <RelatedLink label="Studio" value={name} href={href} />
                  ) : (
                    <Box
                      py={4}
                      borderBottomWidth={index === workLocations.length - 1 ? 0 : "1px"}
                      borderColor={theme.borderGlass.subtle}
                    >
                      <Text fontSize="xs" color={theme.text.tertiary} mb={1}>
                        Studio
                      </Text>
                      <Text fontSize="sm" fontWeight="600" color={theme.text.secondary}>
                        {name}
                      </Text>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Flex>
  );
};
